import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  writeBatch,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Broker } from './BrokerService';

class FirebaseBrokerService {
  private readonly collectionName = 'brokers';
  private readonly collection = collection(db, this.collectionName);

  // Default brokers to initialize the database
  private defaultBrokers: Omit<Broker, 'id'>[] = [
    {
      name: 'Exness',
      description: 'Global multi-asset broker offering forex, commodities, cryptocurrencies, and indices with unlimited leverage and instant withdrawals.',
      website: 'https://www.exness.com',
      logo: 'https://www.exness.com/favicon.ico',
      features: [
        'Unlimited Leverage',
        'Instant Withdrawals', 
        'No Swap on Gold',
        '24/7 Support',
        'Multi-Asset Trading'
      ],
      rating: 4.8,
      minDeposit: '$1',
      leverage: 'Unlimited',
      spreads: 'From 0.3 pips',
      regulation: ['FSA', 'CySEC', 'FCA', 'FSCA'],
      tradingPlatforms: ['MT4', 'MT5', 'Exness Terminal'],
      accountTypes: ['Standard', 'Pro', 'Raw Spread', 'Zero'],
      contactEmail: 'support@exness.com',
      referralLink: 'https://one.exness.link/a/upmtraders',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      name: 'XM Group',
      description: 'Award-winning forex and CFD broker with over 5 million clients worldwide, offering comprehensive trading solutions and education.',
      website: 'https://www.xm.com',
      logo: 'https://www.xm.com/favicon.ico',
      features: [
        'No Re-quotes',
        'No Rejection',
        'Micro Lots',
        'Educational Resources',
        'Multilingual Support'
      ],
      rating: 4.7,
      minDeposit: '$5',
      leverage: 'Up to 1:888',
      spreads: 'From 1 pip',
      regulation: ['CySEC', 'ASIC', 'IFSC'],
      tradingPlatforms: ['MT4', 'MT5', 'XM WebTrader'],
      accountTypes: ['Micro', 'Standard', 'XM Ultra Low'],
      contactEmail: 'support@xm.com',
      referralLink: 'https://clicks.pipaffiliates.com/c?c=550036&l=en&p=0',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      name: 'FP Markets',
      description: 'Australian regulated broker with institutional-grade trading conditions, deep liquidity, and cutting-edge technology.',
      website: 'https://www.fpmarkets.com',
      logo: 'https://www.fpmarkets.com/favicon.ico',
      features: [
        'ECN Execution',
        'Deep Liquidity',
        'Advanced Charting',
        'Copy Trading',
        'VPS Hosting'
      ],
      rating: 4.6,
      minDeposit: '$100',
      leverage: 'Up to 1:500',
      spreads: 'From 0.0 pips',
      regulation: ['ASIC', 'CySEC', 'FSCA'],
      tradingPlatforms: ['MT4', 'MT5', 'cTrader', 'IRESS'],
      accountTypes: ['Standard', 'Raw', 'Professional'],
      contactEmail: 'support@fpmarkets.com',
      referralLink: 'https://www.fpmarkets.com/au/?affiliates=upmtraders',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      name: 'FBS',
      description: 'International broker serving traders worldwide with innovative trading solutions, high leverage, and competitive conditions.',
      website: 'https://www.fbs.com',
      logo: 'https://www.fbs.com/favicon.ico',
      features: [
        'High Leverage',
        'Bonus Programs',
        'CopyTrade',
        'Mobile Trading',
        'Fast Execution'
      ],
      rating: 4.5,
      minDeposit: '$1',
      leverage: 'Up to 1:3000',
      spreads: 'From 0.5 pips',
      regulation: ['CySEC', 'IFSC'],
      tradingPlatforms: ['MT4', 'MT5', 'FBS Trader'],
      accountTypes: ['Cent', 'Standard', 'Zero Spread', 'ECN'],
      contactEmail: 'support@fbs.com',
      referralLink: 'https://fbs.com/?ppu=upmtraders',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      name: 'IC Markets',
      description: 'Leading forex CFD provider with award-winning customer service, tight spreads, and lightning-fast execution.',
      website: 'https://www.icmarkets.com',
      logo: 'https://www.icmarkets.com/favicon.ico',
      features: [
        'Tight Spreads',
        'Fast Execution',
        'Award Winning',
        'True ECN',
        'Professional Tools'
      ],
      rating: 4.9,
      minDeposit: '$200',
      leverage: 'Up to 1:500',
      spreads: 'From 0.0 pips',
      regulation: ['ASIC', 'CySEC', 'FSA'],
      tradingPlatforms: ['MT4', 'MT5', 'cTrader'],
      accountTypes: ['Standard', 'Raw Spread', 'cTrader'],
      contactEmail: 'support@icmarkets.com',
      referralLink: 'https://www.icmarkets.com/?camp=upmtraders',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  ];

  // Initialize database with default brokers if empty
  async initializeDatabase(): Promise<void> {
    try {
      const snapshot = await getDocs(this.collection);
      if (snapshot.empty) {
        const batch = writeBatch(db);
        this.defaultBrokers.forEach((broker) => {
          const docRef = doc(this.collection);
          batch.set(docRef, broker);
        });
        await batch.commit();
        console.log('Database initialized with default brokers');
      }
    } catch (error) {
      console.error('Failed to initialize database:', error);
    }
  }

  // Get all brokers
  async getAllBrokers(): Promise<Broker[]> {
    try {
      await this.initializeDatabase();
      const querySnapshot = await getDocs(query(this.collection, orderBy('createdAt', 'desc')));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Broker));
    } catch (error) {
      console.error('Failed to get brokers:', error);
      return [];
    }
  }

  // Get active brokers only
  async getActiveBrokers(): Promise<Broker[]> {
    try {
      const q = query(this.collection, where('isActive', '==', true), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Broker));
    } catch (error) {
      console.error('Failed to get active brokers:', error);
      return [];
    }
  }

  // Get broker by ID
  async getBrokerById(id: string): Promise<Broker | null> {
    try {
      const docRef = doc(this.collection, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as Broker;
      }
      return null;
    } catch (error) {
      console.error('Failed to get broker by ID:', error);
      return null;
    }
  }

  // Add new broker
  async addBroker(brokerData: Omit<Broker, 'id' | 'createdAt' | 'updatedAt'>): Promise<Broker> {
    try {
      const newBrokerData = {
        ...brokerData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(this.collection, newBrokerData);
      
      return {
        id: docRef.id,
        ...newBrokerData
      } as Broker;
    } catch (error) {
      console.error('Failed to add broker:', error);
      throw error;
    }
  }

  // Update broker
  async updateBroker(id: string, updates: Partial<Omit<Broker, 'id' | 'createdAt' | 'updatedAt'>>): Promise<Broker | null> {
    try {
      const docRef = doc(this.collection, id);
      const updateData = {
        ...updates,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(docRef, updateData);
      
      // Get updated document
      const updatedDoc = await getDoc(docRef);
      if (updatedDoc.exists()) {
        return {
          id: updatedDoc.id,
          ...updatedDoc.data()
        } as Broker;
      }
      return null;
    } catch (error) {
      console.error('Failed to update broker:', error);
      throw error;
    }
  }

  // Delete broker
  async deleteBroker(id: string): Promise<boolean> {
    try {
      const docRef = doc(this.collection, id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Failed to delete broker:', error);
      return false;
    }
  }

  // Toggle broker active status
  async toggleBrokerStatus(id: string): Promise<Broker | null> {
    try {
      const docRef = doc(this.collection, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      const currentData = docSnap.data() as Broker;
      const updateData = {
        isActive: !currentData.isActive,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(docRef, updateData);
      
      return {
        ...currentData,
        ...updateData,
        id: docSnap.id
      } as Broker;
    } catch (error) {
      console.error('Failed to toggle broker status:', error);
      return null;
    }
  }

  // Search brokers
  async searchBrokers(searchTerm: string): Promise<Broker[]> {
    try {
      const allBrokers = await this.getAllBrokers();
      const lowerSearchTerm = searchTerm.toLowerCase();
      
      return allBrokers.filter(broker =>
        broker.name.toLowerCase().includes(lowerSearchTerm) ||
        broker.description.toLowerCase().includes(lowerSearchTerm) ||
        broker.features.some(feature => feature.toLowerCase().includes(lowerSearchTerm))
      );
    } catch (error) {
      console.error('Failed to search brokers:', error);
      return [];
    }
  }

  // Real-time listener for brokers
  onBrokersChange(callback: (brokers: Broker[]) => void): () => void {
    const q = query(this.collection, orderBy('createdAt', 'desc'));
    
    return onSnapshot(q, (querySnapshot) => {
      const brokers = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Broker));
      callback(brokers);
    }, (error) => {
      console.error('Error listening to brokers changes:', error);
    });
  }
}

export const firebaseBrokerService = new FirebaseBrokerService();